import { inject, Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { OnGetDashboardData, OnGetDashboardSalesInvoices, OnGetDashboardSupplierShipments, OnGetLiveShipmentsData, OnSetDashboardGridConfig, OnSwapShipmentView } from './dashboard.actions';
import { DashboardData, DashboardStateModel, LiveShipmentsData } from './dashboard.model';
import { catchError, tap } from 'rxjs';
import { DashboardService } from '../dashboard.service';
import { OnSetSharedGridConfig, OnSetSharedGridRowData } from 'app/modules/shared/ag-grid/shared-grid-state/shared-grid.actions';
import { SetShowLoader, ShowToastrAlert } from 'app/modules/shared-state/shared.actions';
import {
  DashboardSalesInvoiceRow,
  DashboardSupplierShipmentRow,
} from '../models/dashboard-api.models';


@State<DashboardStateModel>({
  name: 'dashboard',
  defaults: {
    dashboardData: {
      cardsData: null,
      stockOverviewData: null,
      topStockDetails: []
    },
    liveShipmentsData: null
  }
})
@Injectable()
export class DashboardState {

  private _dashboardService = inject(DashboardService);

  @Action(OnGetDashboardData)
  onGetDashboardData(ctx: StateContext<DashboardStateModel>) {
    ctx.dispatch(new SetShowLoader(true));
    return this._dashboardService.getDashboardData().pipe(
      catchError((error) => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Error fetching dashboard data: ${error.error.message.message}`,
          position: 'toast-top-center'
        }));
        ctx.dispatch(new SetShowLoader(false));
        throw error;
      }),
      tap((data: DashboardData) => {
        ctx.dispatch(new SetShowLoader(false));
        ctx.patchState({
          dashboardData: {
            cardsData: data.cardsData,
            stockOverviewData: data.stockOverviewData,
            topStockDetails: data.topStockDetails
          }
        });
      })
    );
  }

  @Selector()
  static getDashboardData(state: DashboardStateModel) {
    return state.dashboardData.cardsData;
  }

  @Selector()
  static getStockOverviewData(state: DashboardStateModel) {
    return state.dashboardData.stockOverviewData;
  }

  @Selector()
  static getTopStockDetails(state: DashboardStateModel) {
    return state.dashboardData.topStockDetails;
  }

  @Action(OnSetDashboardGridConfig)
  onSetDashboardGridConfig(ctx: StateContext<DashboardStateModel>, { payload }: OnSetDashboardGridConfig) {
    ctx.dispatch(new OnSetSharedGridConfig(payload));
  }

  @Action(OnGetLiveShipmentsData)
  onGetLiveShipmentsData(ctx: StateContext<DashboardStateModel>, { payload }: OnGetLiveShipmentsData) {
    return this._dashboardService.getLiveShipmentsData(payload.query).pipe(
      catchError((error) => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Error fetching live shipments data: ${error.error.message.message}`,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((data: LiveShipmentsData) => {
        ctx.patchState({
          liveShipmentsData: data
        });

        const rowsForActiveTab = data?.[payload.activeTab] ?? [];
        ctx.dispatch(new OnSetSharedGridRowData({
          gridUUID: payload.gridUUID,
          payload: rowsForActiveTab,
          reload: true,
        }));
      })
    );
  }

  @Selector()
  static getLiveShipmentsData(state: DashboardStateModel) {
    return state.liveShipmentsData;
  }

  @Action(OnSwapShipmentView)
  onSwapShipmentView(ctx: StateContext<DashboardStateModel>, { payload }: OnSwapShipmentView) {
    const liveShipmentsData = ctx.getState().liveShipmentsData;

    if (!liveShipmentsData) return;

    const rowsForTab = liveShipmentsData?.[payload.tab] ?? [];
    this.dispatchLiveShipmentsGridRows(ctx, payload.gridUUID, rowsForTab, 200);
  }

  @Action(OnGetDashboardSupplierShipments)
  onGetDashboardSupplierShipments(ctx: StateContext<DashboardStateModel>, { payload }: OnGetDashboardSupplierShipments) {
    return this._dashboardService.getDashboardSupplierShipments(payload.dateRange).pipe(
      catchError((error) => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Error fetching purchase invoices data: ${error.error.message.message}`,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((data: DashboardSupplierShipmentRow[]) => {
        ctx.dispatch(new OnSetSharedGridRowData({
          gridUUID: payload.gridUUID,
          payload: data,
          reload: true,
        }));
      })
    );
  }

  @Action(OnGetDashboardSalesInvoices)
  onGetDashboardSalesInvoices(ctx: StateContext<DashboardStateModel>, { payload }: OnGetDashboardSalesInvoices) {
    return this._dashboardService.getDashboardSalesInvoices(payload.dateRange).pipe(
      catchError((error) => {
        ctx.dispatch(new ShowToastrAlert({
          showToast: true,
          toastrType: 'error',
          messageTitle: 'Error',
          messageBody: `Error fetching sales invoices data: ${error.error.message.message}`,
          position: 'toast-top-center'
        }));
        throw error;
      }),
      tap((data: DashboardSalesInvoiceRow[]) => {
        ctx.dispatch(new OnSetSharedGridRowData({
          gridUUID: payload.gridUUID,
          payload: data,
          reload: true,
        }));
      })
    );
  }

  private dispatchLiveShipmentsGridRows(ctx: StateContext<DashboardStateModel>, gridUUID: string, rows: any[], delayMs = 0): void {
    const updateGridRows = () => {
      ctx.dispatch(new OnSetSharedGridRowData({
        gridUUID,
        payload: rows,
        reload: true,
      }));
    };

    if (delayMs > 0)  {
      setTimeout(updateGridRows, delayMs);
    } else {
      updateGridRows()
    }
  }
}